import React from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../services/api';
import { FlashcardDeckSummary } from '../../types';
import Navbar from '../shared/Navbar';

const FlashcardsPage: React.FC = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = React.useState<FlashcardDeckSummary | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    const loadSummary = async () => {
      setLoading(true);
      setError(null);
      try {
        setSummary(await api.getFlashcardDeckSummary());
      } catch (e: any) {
        setError(e?.message || 'Failed to load your flashcard deck');
      } finally {
        setLoading(false);
      }
    };

    void loadSummary();
  }, []);

  const openReview = (courseId?: number, courseTitle?: string) => {
    const params = new URLSearchParams();
    if (courseId != null) params.set('courseId', String(courseId));
    if (courseTitle) params.set('courseTitle', courseTitle);
    const query = params.toString();
    navigate(`/flashcards/review${query ? `?${query}` : ''}`);
  };

  return (
    <div className="app">
      <Navbar />
      <main className="app-main">
        <div className="flashcards-page">
          <div className="flashcards-study-header">
            <div>
              <h2>Your Flashcard Deck</h2>
              <p>Choose what you want to study.</p>
            </div>
          </div>

          {loading && <div className="tm-surface flashcards-study-card">Loading your deck...</div>}

          {error && (
            <div className="tm-surface flashcards-study-card flashcards-study-error">
              {error === 'Authentication required'
                ? 'Sign in to view your flashcard deck.'
                : error}
            </div>
          )}

          {!loading && !error && summary && (
            <div className="flashcards-choice-list">
              <button
                type="button"
                className="flashcards-choice-card flashcards-choice-card--all"
                disabled={summary.total_count === 0}
                onClick={() => openReview()}
              >
                <span>
                  <strong>Study all flashcards</strong>
                  <small>Your complete deck across every course</small>
                </span>
                <span className="flashcards-choice-count">{summary.total_count}</span>
              </button>

              {summary.courses.map(course => (
                <button
                  key={course.course_id}
                  type="button"
                  className="flashcards-choice-card"
                  disabled={course.deck_count === 0}
                  onClick={() => openReview(course.course_id, course.course_title)}
                >
                  <span>
                    <strong>{course.course_title}</strong>
                    <small>Study this course</small>
                  </span>
                  <span className="flashcards-choice-count">{course.deck_count}</span>
                </button>
              ))}

              {summary.total_count === 0 && (
                <div className="tm-surface flashcards-study-card flashcards-study-empty">
                  <h3>Your deck is empty</h3>
                  <p>Add flashcards from a course page or complete lesson sections to build your deck.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default FlashcardsPage;
